import { useState } from 'react';
import { AIProvider } from '../types/ai';
import { Assistant } from '../types/assistant';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Edit2, Users, Globe, Lock, ChevronLeft, Share2, Plus } from 'lucide-react';
import { useMobile } from '../hooks/use-mobile';

interface ChatHeaderProps {
  provider: AIProvider;
  availableProviders: AIProvider[];
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  onProviderChange: (providerId: string) => void;
  // Props opcionais para agentes
  assistant?: Assistant;
  onEditAssistant?: () => void;
  onShareAssistant?: () => void;
  onBackClick?: () => void;
  onNewConversation?: () => void;
}

export function ChatHeader({ 
  provider, 
  availableProviders, 
  selectedModel, 
  onModelChange, 
  onProviderChange,
  assistant,
  onEditAssistant,
  onShareAssistant,
  onBackClick,
  onNewConversation
}: ChatHeaderProps) {
  const [isModelChanged, setIsModelChanged] = useState(false);
  const [isProviderChanged, setIsProviderChanged] = useState(false);
  const currentModel = provider.models.find(m => m.id === selectedModel);
  const isMobile = useMobile();
  
  // Renderizar header especial para agentes
  if (assistant) {
    // 📱 Layout Mobile: < | Nome do agente | Compartilhar | Editar
    if (isMobile) {
      return (
        <div className="border-b border-gray-200 bg-white fixed top-0 left-0 right-0 z-50">
          <div className="flex items-center justify-between p-3 gap-2">
            {/* Botão Voltar */}
            {onBackClick && (
              <button
                onClick={onBackClick}
                className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
            )}
            
            {/* Avatar + Nome do Agente (truncado) */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-lg flex-shrink-0">
                {assistant.avatar || '🤖'}
              </div>
              <h2 className="text-base font-semibold text-gray-900 truncate">{assistant.name}</h2>
            </div>

            {/* Ícones de Ação */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Botão Nova Conversa */}
              {onNewConversation && (
                <button
                  onClick={onNewConversation}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Nova Conversa"
                >
                  <Plus className="w-5 h-5 text-gray-700" />
                </button>
              )}

              {/* Ícone de Compartilhar */}
              {onShareAssistant && (
                <button
                  onClick={onShareAssistant}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Compartilhar"
                >
                  <Share2 className="w-5 h-5 text-gray-700" />
                </button>
              )}
              
              {/* Ícone de Editar */}
              {onEditAssistant && (
                <button
                  onClick={onEditAssistant}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Editar"
                >
                  <Edit2 className="w-5 h-5 text-gray-700" />
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    // 💻 Layout Desktop (sticky funciona melhor)
    return (
      <div className="border-b border-gray-200 px-4 py-2 bg-white sticky top-0 z-50">
        <div className="flex items-center justify-between">
          {/* Lado esquerdo: Avatar e nome do agente */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-lg">{assistant.avatar || '🤖'}</span>
              <h2 className="text-base font-semibold text-gray-900">{assistant.name}</h2>
            </div>
            {assistant.description && (
              <p className="text-xs text-gray-500 line-clamp-1 max-w-md">{assistant.description}</p>
            )}
          </div>

          {/* Lado direito: Botões de ação */}
          <div className="flex items-center space-x-1 lg:space-x-2">
            {/* Botão Nova Conversa */}
            {onNewConversation && (
              <Button
                variant="outline"
                size="sm"
                onClick={onNewConversation}
                className="flex items-center gap-1 lg:gap-2 px-2 lg:px-3"
                title="Nova Conversa"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden lg:inline">Nova Conversa</span>
              </Button>
            )}

            {/* Botão de compartilhamento (mostra estado atual) */}
            {onShareAssistant && (
              <Button
                variant="outline"
                size="sm"
                onClick={onShareAssistant}
                className="flex items-center gap-1 lg:gap-2 px-2 lg:px-3"
                title="Compartilhar"
              >
                {assistant.visibility === 'team' && (
                  <>
                    <Users className="w-4 h-4" />
                    <span className="hidden lg:inline">Toda Empresa</span>
                  </>
                )}
                {assistant.visibility === 'shared' && (
                  <>
                    <Users className="w-4 h-4" />
                    <span className="hidden lg:inline">Compartilhar</span>
                  </>
                )}
                {assistant.isPublic && (
                  <>
                    <Globe className="w-4 h-4" />
                    <span className="hidden lg:inline">Público</span>
                  </>
                )}
                {!assistant.isPublic && (!assistant.visibility || assistant.visibility === 'private') && (
                  <>
                    <Lock className="w-4 h-4" />
                    <span className="hidden lg:inline">Compartilhar</span>
                  </>
                )}
              </Button>
            )}

            {/* Botão de editar */}
            {onEditAssistant && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEditAssistant}
                className="flex items-center gap-1 lg:gap-2 px-2 lg:px-3"
                title="Editar"
              >
                <Edit2 className="w-4 h-4" />
                <span className="hidden lg:inline">Editar</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  const handleModelChange = (newModelId: string) => {
    if (newModelId !== selectedModel) {
      setIsModelChanged(true);
      onModelChange(newModelId);
      
      // Remover o indicador após 3 segundos
      setTimeout(() => {
        setIsModelChanged(false);
      }, 3000);
    }
  };

  const handleProviderChange = (newProviderId: string) => {
    if (newProviderId !== provider.id) {
      setIsProviderChanged(true);
      onProviderChange(newProviderId);
      
      // Remover o indicador após 3 segundos
      setTimeout(() => {
        setIsProviderChanged(false);
      }, 3000);
    }
  };
  
  return (
    <div className="border-b border-gray-200 p-2 bg-white min-h-[36px] sticky top-0 z-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {provider.id === 'openai' ? (
            <img src="/gpt-logo.png?v=1" alt="GPT Logo" className="w-6 h-6 object-contain" />
          ) : provider.id === 'anthropic' ? (
            <img src="/claude-logo.png?v=1" alt="Claude Logo" className="w-6 h-6 object-contain" />
          ) : provider.id === 'google' ? (
            <img src="/gemini-logo.png?v=1" alt="Gemini Logo" className="w-6 h-6 object-contain" />
          ) : provider.id === 'perplexity' ? (
            <img src="/perplexity-logo.png?v=1" alt="Perplexity Logo" className="w-6 h-6 object-contain" />
          ) : provider.id === 'grok' ? (
            <img src="/grok-logo.png" alt="Grok Logo" className="w-6 h-6" />
          ) : provider.id === 'deepseek' ? (
            <img src="/deepseek-logo.png" alt="DeepSeek Logo" className="w-6 h-6" />
          ) : (
            <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
              <span className="text-base">{provider.icon}</span>
            </div>
          )}
          
          {/* Nome do chat com seletor de IA integrado */}
          <Select value={provider.id} onValueChange={handleProviderChange}>
            <SelectTrigger showArrow={false} className="w-auto h-auto p-0 border-0 bg-transparent hover:bg-gray-50 rounded text-base font-medium text-gray-900 transition-colors duration-200 focus:ring-0 focus:outline-none">
              <SelectValue>
                <div className="flex items-center">
                  <span className="truncate">{provider.name}</span>
                  <svg className="w-4 h-4 ml-1 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="min-w-[200px]">
              {availableProviders.map((provider) => (
                <SelectItem key={provider.id} value={provider.id} className="text-sm py-3 px-4 hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {provider.id === 'openai' ? (
                        <img src="/gpt-logo.png?v=1" alt="GPT Logo" className="w-4 h-4 object-contain" />
                      ) : provider.id === 'anthropic' ? (
                        <img src="/claude-logo.png?v=1" alt="Claude Logo" className="w-4 h-4 object-contain" />
                      ) : provider.id === 'google' ? (
                        <img src="/gemini-logo.png?v=1" alt="Gemini Logo" className="w-4 h-4 object-contain" />
                      ) : provider.id === 'perplexity' ? (
                        <img src="/perplexity-logo.png?v=1" alt="Perplexity Logo" className="w-4 h-4 object-contain" />
                      ) : provider.id === 'grok' ? (
                        <img src="/grok-logo.png" alt="Grok Logo" className="w-4 h-4" />
                      ) : provider.id === 'deepseek' ? (
                        <img src="/deepseek-logo.png" alt="DeepSeek Logo" className="w-4 h-4" />
                      ) : (
                        <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                          <span className="text-xs">{provider.icon}</span>
                        </div>
                      )}
                    </div>
                    <span className="truncate">{provider.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Seletor de Modelo */}
          <Select value={selectedModel} onValueChange={handleModelChange}>
            <SelectTrigger showArrow={false} className="w-40 h-8 text-xs border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 rounded-md shadow-sm transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <SelectValue>
                <div className="flex items-center justify-between w-full">
                  <span className="truncate">{currentModel?.name || 'Selecionar modelo'}</span>
                  <svg className="w-3 h-3 text-gray-400 flex-shrink-0 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="min-w-[180px]">
              {provider.models.map((model) => (
                <SelectItem key={model.id} value={model.id} className="text-xs py-2 px-3 hover:bg-gray-50">
                  <span className="truncate">{model.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Aviso sobre mudança de modelo */}
      {isModelChanged && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-blue-600">🔄</span>
            <div className="text-sm">
              <span className="font-medium text-blue-900">Modelo alterado para {currentModel?.name}</span>
              <p className="text-blue-700 mt-1">
                Suas próximas mensagens serão processadas pelo novo modelo. 
                As mensagens anteriores permanecem inalteradas.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Aviso sobre mudança de IA */}
      {isProviderChanged && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-green-600">🤖</span>
            <div className="text-sm">
              <span className="font-medium text-green-900">IA alterada para {provider.name}</span>
              <p className="text-blue-700 mt-1">
                Suas próximas mensagens serão processadas pela nova IA. 
                As mensagens anteriores permanecem inalteradas.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}